/**
 * GELİŞTİRMEYE GÖNDER (kullanıcı kararı 25.10.2026): okunamayan satırlar ya
 * da ekstre biçimi, kullanıcı gönderilecek metni BİREBİR görüp "Gönder"
 * derse sunucuya gider. Düğmeye basılmadan hiçbir şey gitmez; metin
 * değişirse (kelime açıp kapatma) önceki onay geçmez, yeniden gönderilir.
 */

import { useState, type ReactNode } from "react";
import { GERI_BILDIRIM_SINIRLARI, geriBildirimDenetle, type GeriBildirimKaynagi } from "@parafon/contracts/central-api";
import { merkezKasaIstegi, merkezTabanAdresi } from "../central/createCentral";
import { geriBildirimGonder, geriBildirimKoduGoster, type GeriBildirimIstegi } from "../lib/geriBildirim";
import { M } from "../dil";
import { errorText } from "../lib/rpcError";
import { Button, Notice } from "../ui/primitives";

type Durum =
  | { readonly tur: "kapali" }
  | { readonly tur: "acik" }
  | { readonly tur: "gonderiliyor" }
  | { readonly tur: "tamam"; readonly kod: string; readonly metin: string }
  | { readonly tur: "hata"; readonly mesaj: string };

function varsayilanIstek(): GeriBildirimIstegi | null {
  const taban = merkezTabanAdresi();
  return taban === null ? null : merkezKasaIstegi(taban);
}

export function GeriBildirimKarti({
  kaynak,
  metin,
  istek,
  children,
}: {
  readonly kaynak: GeriBildirimKaynagi;
  /** Gönderilecek metnin tamamı; ekranda birebir gösterilir. */
  readonly metin: string;
  /** Testler sahte istek verir; verilmezse cihaz çekirdeğinin taşıyıcısı. */
  readonly istek?: GeriBildirimIstegi | null;
  /** Önizlemenin üstünde (ör. kelime seçimi). */
  readonly children?: ReactNode;
}) {
  const [durum, setDurum] = useState<Durum>({ tur: "kapali" });
  const sebep = geriBildirimDenetle(kaynak, metin);
  const gonderildi = durum.tur === "tamam" && durum.metin === metin;

  const gonder = async (): Promise<void> => {
    const i = istek === undefined ? varsayilanIstek() : istek;
    if (i === null) {
      setDurum({ tur: "hata", mesaj: M.iceAktar.geriBildirim.merkezYok });
      return;
    }
    setDurum({ tur: "gonderiliyor" });
    try {
      const kod = await geriBildirimGonder(i, kaynak, metin);
      setDurum({ tur: "tamam", kod, metin });
    } catch (e: unknown) {
      setDurum({ tur: "hata", mesaj: errorText(e) });
    }
  };

  if (durum.tur === "kapali") {
    return (
      <div>
        <Button onClick={() => setDurum({ tur: "acik" })}>{M.iceAktar.geriBildirim.ac}</Button>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-2 rounded-pf border border-line p-3" role="group" aria-label={M.iceAktar.geriBildirim.grup}>
      <p className="text-[12px] text-muted">
        {M.iceAktar.geriBildirim.aciklama(GERI_BILDIRIM_SINIRLARI.saklamaGun)}
      </p>
      {children}
      <pre
        aria-label={M.iceAktar.geriBildirim.metin}
        className="max-h-72 max-w-full overflow-auto rounded-pf border border-line p-2 font-mono text-[11px] leading-snug whitespace-pre text-ink"
      >
        {metin}
      </pre>
      {sebep !== null && (
        <Notice tone="warn" title={M.iceAktar.geriBildirim.gonderilemezBaslik}>
          <p>
            {/* dil-disi: sözleşmenin sebep değeriyle karşılaştırma */}
            {sebep === "metin çok büyük"
              ? M.iceAktar.geriBildirim.sinirAsiyor
              : M.iceAktar.geriBildirim.sebep(sebep)}
          </p>
        </Notice>
      )}
      {gonderildi ? (
        <Notice tone="info" title={M.iceAktar.geriBildirim.gonderildi}>
          <p>
            {M.iceAktar.geriBildirim.basvuruKodu}{" "}
            <span className="pf-fig whitespace-nowrap">{geriBildirimKoduGoster(durum.kod)}</span>
            {M.iceAktar.geriBildirim.buKodla}
          </p>
        </Notice>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            disabled={sebep !== null || durum.tur === "gonderiliyor"}
            onClick={() => void gonder()}
          >
            {durum.tur === "gonderiliyor" ? M.iceAktar.geriBildirim.gonderiliyor : M.iceAktar.geriBildirim.gonder}
          </Button>
          <Button onClick={() => setDurum({ tur: "kapali" })}>{M.iceAktar.geriBildirim.vazgec}</Button>
        </div>
      )}
      {durum.tur === "hata" && (
        <Notice tone="danger" title={M.iceAktar.geriBildirim.gonderilemedi}>
          <p>{durum.mesaj}</p>
        </Notice>
      )}
    </div>
  );
}
